import { useEffect, useMemo, useRef, useState } from 'react';
import { DebtProductRowData, Metric, VisibilityStates } from './debtProductTypes';
import { useAppSelector } from '../../app/hooks';
import { USER_TYPE } from '../../utils/constants/user';
import { fetchConfigByConfigName } from '../../store/investor/investorApiHelper';
import { fetchAllPecData, getDealFormConfig, getDealsByStatusData } from '../../store/investor/apiHelpers';
import { getCompanyDealMetrics, getCompanyMetrics, getDebtProductsData } from '../../store/debtProducts/apiHelper_debtProducts';
import { Mixpanel } from '../../utils/mixpanel';
import {
    AICA_CREATE_DEAL,
    AICA_SHARE_DEAL,
    CLICK_PROBABILITY_HELP,
    EXPAND_PRODUCT,
    OPEN_EDIT_PROFILE,
    VIEW_DEBT_PRODUCTS_PAGE,
} from '../../utils/constants/mixpanelEvents/investorEvents';
import { getTimeStamp } from '../../utils/dateUtils';
import { convertToFirstLetterCapital, deepCompare, getPlatformUserId, getSubdomain, isEmpty, isLenderType } from '../../utils/utils';
import { useLocation, useSearchParams } from 'react-router-dom';
import { fetchCompanyUploadConfig, fetchUploadJourneyData, setDebtProductsFilters } from '../../store/company/action';
import { useDispatch } from 'react-redux';
import useDADataTransformations, { MasterData } from '../../common/DealApplicationDrawer/customHooks/useDADataTransformations';
import {
    createDeal,
    fetchLenderConfig,
    getDealData,
    handleDealApplicationDrawer,
    setDealStatusConfig,
    updateDeal,
    updateDealApplicationData,
    setCreateMandateModal,
    setConfigTableModal,
    updateCompany,
} from '../../store/investor/action';
import { fetchDataByConfig } from '../../common/ManageDealDrawer/ManageDealDrawerService';
import { polusBaseUrl } from '../../utils/axios-interceptor';
import { DEAL_CREATOR_TYPE } from '../../common/ManageDealDrawer/DealDrawerConstants';
import { Button } from '../../common/_custom';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';
import { plusWhite, ShareIcon, infoIconRed, infoIconGreen, greenCheck, errorIconRed } from '../../assets/hostedassets';
import { updateQueryParams } from '../../utils/searchParamUtils';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { useVisibilityConfig } from '../../utils/useVisibilityConfig';
import { DISPATCH_EVENTS, MANDATE_STATES, QUALIFICATION_STATUS, VIEW_CONFIGS } from '../../utils/constants/commonConstants';
import { getMandateQualificationChecksData } from '../../store/investor/apiHelpers';
import { DealStatus } from '../../store/investor/reducer';
import styles from './DebtProducts.module.scss';
import { ENTITY_TYPES } from '../../store/investor/constants';
interface DebtProductsState {
    headers: Array<{ id: string; label: string }>;
    rows: DebtProductRowData[];
    visibilityStates: VisibilityStates;
}

export const AUDITED_FINANCIAL_KEY_METRIC: Metric[] = [
    'Revenue',
    'RevenueGrowthPercentage',
    'EbitaMargin',
    'PAT',
    'CashAndCashEquivalent',
    'TotalCurrentAssets',
    'TotalCurrentLiabilities',
    'NetWorkingCapital',
    'LongTermDebt',
    'ShortTermDebt',
    'TangibleNetWorth',
    'TotalCosts',
    'TradePayables',
    'TradeReceivables',
    'Inventories',
];

const useDebtProductLogic = ({ params, selectedCompany }) => {
    // Probability Tooltip Constants
    const PROBABILITY_TOOLTIP_MESSAGES = {
        LOW_SCORE: 'A low score is less likely to attract lender interest. \nClick for more details. ',
        HIGH_SCORE: 'A high score is more likely to attract lender interest. \nClick for more details. ',
    };

    /**
     * Generate tooltip content for probability score
     * @param probability - The probability value as a string
     * @param pecThreshold - Optional PEC threshold percentage as a string
     * @returns Tooltip content object or null if threshold is not present
     */
    const getProbabilityTooltip = (probability: string, pecThreshold?: string) => {
        // Don't show tooltip if threshold is not present
        if (!pecThreshold) {
            return null;
        }

        const probabilityValue = parseFloat(probability);
        const probabilityNum = Number.isNaN(probabilityValue) ? 0 : probabilityValue;

        const pecThresholdValue = parseFloat(pecThreshold);
        const pecThresholdNum = Number.isNaN(pecThresholdValue) ? 0 : pecThresholdValue;

        const isBelowThreshold = probabilityNum < pecThresholdNum;

        return {
            isBelowThreshold,
            message: isBelowThreshold ? PROBABILITY_TOOLTIP_MESSAGES.LOW_SCORE : PROBABILITY_TOOLTIP_MESSAGES.HIGH_SCORE,
            recommendedPercentage: isBelowThreshold ? `${pecThresholdNum}%` : null,
        };
    };
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const location = useLocation();
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);
    const [showDetailOverview, setShowDetailOverview] = useState<null | DebtProductRowData>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [updatedLendersMap, setUpdatedLendersMap] = useState<[string, string[]][]>([]);
    const { isVisible } = useVisibilityConfig();
    const [debtProductsData, setDebtProductsData] = useState<DebtProductsState>({
        headers: [],
        rows: [],
        visibilityStates: {
            debtProductName: 'Debt Products',
        },
    });
    const dealApplicationDrawerState = useAppSelector((state) => state.investor.dealApplicationDrawer);
    const [noPecExists, setNoPECExists] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [defaultFilters, setDefaultFilters] = useState({});
    const [companyMetricsDataExists, setCompanyMetricsDataExists] = useState(false);
    const [companyMetricsData, setCompanyMetricsData] = useState<any>({});
    const [percentageCompletion, setPercentageCompletion] = useState<any>(undefined);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [mandateState, setMandateState] = useState(null);
    const [dealPayload, setDealPayload] = useState<{
        products: string[];
        lenders: Map<string, string[]>;
    }>({
        products: [],
        lenders: new Map(),
    });
    const originalDealPayloadRef = useRef<{
        products: string[];
        lenders: Map<string, string[]>;
    }>({
        products: [],
        lenders: new Map(),
    });

    const dealPayloadRef = useRef<{
        dealPayload: {
            products: string[];
            lenders: Map<string, string[]>;
        };
        dealId: string;
        mandateState: string | null;
        dealStatus: string;
    }>({
        dealPayload: {
            products: [],
            lenders: new Map(),
        },
        dealId: '',
        mandateState: null,
        dealStatus: '',
    });
    const [selectionCount, setSelectionCount] = useState(0);
    const [draftSelectionCount, setDraftSelectionCount] = useState(0);
    const [isInitialSharedState, setIsInitialSharedState] = useState(false);
    const debtProductsFilters = useAppSelector((state) => state.company.debtProductsFilters);

    const showEmptyScreen = noPecExists || !companyMetricsDataExists;

    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const user = useAppSelector((state) => state.user);
    const dealProducts = useAppSelector((state) => state.investor.dealApplicationDrawer.allProducts);
    const dashboardConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config);
    const loggedInUser = user?.userData;
    const [savingDraft, setSavingDraft] = useState(false);
    const [draftDealsData, setDraftDealsData] = useState([]);
    const [selectedMandate, setSelectedMandate] = useState<any>(null);
    const [dealId, setDealId] = useState('');
    const [dealStatus, setDealStatus] = useState('');
    const [mandateQualificationChecksData, setMandateQualificationChecksData] = useState<any>({ result: false, rules: [] });

    const isLender = isLenderType();
    const isCompany = user.userData.accountType === USER_TYPE.COMPANY || location.pathname.includes('company');
    const { createDealData } = useDADataTransformations({ source: 'debtProducts' });
    const platformUserId = getPlatformUserId();

    // Mandate statuses that can be visible in the mandate dropdown
    const validMandateDropdownStatuses = useMemo(() => {
        return (
            (dealStatusConfig?.mandate as any[])
                ?.filter((item: any) => item.mandateDropdownVisibility === true)
                ?.map((item: any) => item.value) || []
        );
    }, [dealStatusConfig]);

    const tooltipContent = useMemo(() => {
        if (!mandateQualificationChecksData?.rules?.length) return null;

        return (
            <div style={{ minWidth: '300px' }}>
                {mandateQualificationChecksData?.rules?.map((rule: any, index: number) => (
                    <div
                        key={index}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.75rem',
                        }}
                    >
                        <div className={styles.RulesMessage}>
                            <img
                                src={rule.result === true ? greenCheck : errorIconRed}
                                alt={rule.result === true ? QUALIFICATION_STATUS.QUALIFIED : QUALIFICATION_STATUS.UNQUALIFIED}
                                style={{ height: '1rem', width: '1rem' }}
                            />
                        </div>
                        <span className={styles.RulesMessageText}>{rule.message}</span>
                    </div>
                ))}
            </div>
        );
    }, [mandateQualificationChecksData?.rules]);

    const pillStyles = useMemo(
        () => ({
            display: 'flex' as const,
            alignItems: 'center' as const,
            gap: '0.5rem',
            padding: '0.5rem',
            borderRadius: '1rem',
            fontSize: '0.75rem',
            fontFamily: "'Gilroy-Medium', sans-serif",
            fontWeight: 500,
            lineHeight: '1.25rem',
            backgroundColor: mandateQualificationChecksData?.result === false ? '#FFEEEE' : '#E1FFE1',
            color: mandateQualificationChecksData?.result === false ? '#DF666A' : '#49A71B',
            border: 'none',
            whiteSpace: 'nowrap' as const,
            cursor: mandateQualificationChecksData?.rules?.length > 0 ? 'pointer' : 'default',
            boxShadow: '0px 0px 8px 0.5px #00000026',
        }),
        [mandateQualificationChecksData?.rules, mandateQualificationChecksData?.result],
    );

    useEffect(() => {
        Mixpanel.track(VIEW_DEBT_PRODUCTS_PAGE, {
            user_email: user.userData.primaryEmail,
            timestamp: getTimeStamp(),
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
        });
    }, []);

    const getAllProducts = () => {
        const payload = {
            aggregatorId: loggedInUser?.investorOrgId,
        };
        fetchDataByConfig(payload, 'GET_ALL_PRODUCTS', 'GET', polusBaseUrl).then((response) => {
            if (response) {
                const res = response.data.responseBody.data;
                let option = Object.values(res)?.map((option: any) => {
                    return { label: option.productName, value: option.productId };
                });
                dispatch(updateDealApplicationData({ allProducts: option }));
            }
        });
    };

    useEffect(() => {
        if (!dealProducts) {
            getAllProducts();
        }
    }, []);

    // Fetches MANDATE deals data
    useEffect(() => {
        getDealsByStatusData(
            { orgId: params.id as string, status: validMandateDropdownStatuses },
            {
                onSuccess: (data) => {
                    setDraftDealsData(data);
                    // Set first mandate as selected and its dealId
                    if (data && data.length > 0) {
                        setSelectedMandate(data[0]);
                        setDealId(data[0].id);
                    } else {
                        setSelectedMandate(null);
                        setDealId('');
                    }
                },
            },
        );
    }, [params.id, validMandateDropdownStatuses]);

    useEffect(() => {
        dealPayloadRef.current = {
            dealPayload,
            dealId,
            mandateState,
            dealStatus,
        };
    }, [dealPayload, dealId, mandateState, dealStatus]);

    useEffect(() => {
        if (mandateState === MANDATE_STATES.SHARED) {
            setIsInitialSharedState(true);
        }
    }, [mandateState]);

    useEffect(() => {
        if (selectedMandate && !dealDrawerState.open) {
            updateDealStatusOnMandateQualification(selectedMandate?.id || '');
        } else {
            setMandateQualificationChecksData({ result: false, rules: [] });
        }
    }, [dealDrawerState.open, updatedLendersMap]);

    useEffect(() => {
        const handleRequirementUpdated = () => {
            const currentData = dealPayloadRef.current;

            if (!currentData.dealId) {
                return;
            }

            updateDealStatusOnMandateQualification(
                currentData.dealId,
                {
                    mandateState: currentData.mandateState,
                    dealStatus: currentData.dealStatus,
                },
                {
                    products: currentData.dealPayload.products,
                    lenders: currentData.dealPayload.lenders,
                },
            );
        };

        window.addEventListener(DISPATCH_EVENTS.REQUIREMENT_UPDATED, handleRequirementUpdated);

        return () => {
            window.removeEventListener(DISPATCH_EVENTS.REQUIREMENT_UPDATED, handleRequirementUpdated);
        };
    }, []);

    // Fetches deal status config for mandate deals' statuses
    useEffect(() => {
        getDealFormConfig(
            {
                accountType: user.userData.accountType,
                investorOrgId: user.userData.investorOrgId,
                primaryEmail: user.userData.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (data) => {
                    dispatch(setDealStatusConfig(data?.dealStatusDropdownOptions || {}));
                },
            },
        );
    }, [user.userData.accountType, user.userData.investorOrgId, user.userData.primaryEmail]);

    const fetchDraftDeal = () => {
        dispatch(
            getDealData(
                {
                    dealId: dealId,
                },
                {
                    onSuccess: (res) => {
                        let lenderMap = new Map();
                        Object.keys(res?.dealReceiverDetailMap)?.forEach((key) => {
                            lenderMap.set(key, res?.dealReceiverDetailMap[key]?.products);
                        });
                        setDealPayload({
                            products: res?.products,
                            lenders: lenderMap,
                        });
                        originalDealPayloadRef.current = {
                            products: [...res?.products],
                            lenders: new Map(
                                Array.from(lenderMap.entries()).map(([lenderId, productList]) => [lenderId, [...productList]]),
                            ),
                        };

                        setMandateState(res?.mandateState);

                        if (!isEmpty(res.dealTerms) && res.dealStatus !== 'DRAFT_DELETED') {
                            setCompanyMetricsData({ dealTerms: res.dealTerms });
                        }
                        setDealId(res?.id);

                        updateDealStatusOnMandateQualification(res?.id, res, {
                            products: res?.products,
                            lenders: lenderMap,
                        });

                        setDealStatus(res?.dealStatus);
                        // Calculate the number of checkboxes selected
                        if (res.dealStatus !== 'DRAFT_DELETED') {
                            let masterProductList = new Set(res.products.map((product) => product));
                            let lenderCount = 0;
                            Object.keys(res?.dealReceiverDetailMap)?.forEach((key) => {
                                res?.dealReceiverDetailMap[key]?.products.forEach((product) => {
                                    masterProductList.add(product);
                                    lenderCount++;
                                });
                            });
                            setSelectionCount(masterProductList.size + lenderCount);
                            setDraftSelectionCount(masterProductList.size + lenderCount);
                        }
                    },
                    onError: () => {
                        setDealPayload({
                            products: [],
                            lenders: new Map(),
                        });
                        setSelectionCount(0);
                        setDraftSelectionCount(0);
                        setDealStatus('');
                        setMandateState(null);
                    },
                },
            ),
        );
    };

    /**
     * Handles mandate selection from the dropdown
     *
     * @param mandate - Selected mandate object or null for clearing selection
     *
     * Behavior:
     * - Sets selected mandate and dealId when a mandate is selected
     * - Clears selection when cross button is clicked (mandate is null)
     * - Fetches latest metrics data when clearing selection to allow creating new mandate
     */
    const handleMandateSelection = (mandate: any) => {
        setSelectedMandate(mandate);
        setDealId(mandate?.id || '');
        dispatch(setDebtProductsFilters(defaultFilters));
        fetchPECDataAndDebtProductsData(defaultFilters);
        fetchCompanyMetrics(false);
        updateQueryParams(searchParams, setSearchParams, { filters: true }, location);
        setUpdatedLendersMap([]);
        dispatch(updateDealApplicationData({ updatedLendersMap: [] }));
        // Fetch latest metrics when clearing selection for new mandate creation
        if (isEmpty(mandate)) {
            fetchCompanyMetrics(true);
        }
    };

    // Auto-call fetchDraftDeal when dealId changes
    useEffect(() => {
        fetchDraftDeal();
    }, [dealId]);

    // To handle Debt Products page on company side to avoid Unauthorized error
    useEffect(() => {
        if (isCompany) {
            dispatch(
                fetchUploadJourneyData({
                    accountType: user.userData.accountType,
                    assesseeOrgId: params.id as string,
                }),
            );

            dispatch(
                fetchCompanyUploadConfig({
                    accountType: user.userData.accountType,
                    primaryEmail: user.userData.primaryEmail ?? user.userData.investorPrimaryEmail,
                    investorOrgId: user.userData.investorOrgId,
                    configName: 'AICA_BORROWER_ONBOARDING_DATA_BY_BORROWER',
                    assesseeOrgId: params.id as string,
                }),
            );
        }
    }, [user.userData.accountType, user.userData.primaryEmail, user.userData.investorPrimaryEmail]);

    useEffect(() => {
        if (!dealApplicationDrawerState.open && mandateState !== MANDATE_STATES.SHARED) {
            fetchDraftDeal();
        }
    }, [dealApplicationDrawerState.open]);

    useEffect(() => {
        if (searchParams.get('filters') !== 'true') {
            dispatch(setDebtProductsFilters({}));
        }
    }, [searchParams.get('filters')]);

    useEffect(() => {
        if (user.userData.investorOrgId && dashboardConfig?.additionalDetailsConfig && !configLoaded) {
            fetchHeadersFromConfig();
            const isEditProfile = dealDrawerState?.drawerLabel === 'Edit Profile';
            fetchCompanyMetrics(false, isEditProfile);
        }
    }, [user.userData.investorOrgId, dashboardConfig?.additionalDetailsConfig, configLoaded, searchParams.get('draftDeleteUpdated')]);

    useEffect(() => {
        if (configLoaded) {
            fetchPECDataAndDebtProductsData(isEmpty(defaultFilters) ? null : defaultFilters);
            fetchDraftDeal();
        }
    }, [configLoaded, defaultFilters]);

    const fetchHeadersFromConfig = () => {
        fetchConfigByConfigName({
            accountType: user.userData.accountType,
            investorOrgId: user.userData.investorOrgId,
            primaryEmail: user.userData.primaryEmail,
            assesseeOrgId: params.id as string,
            userRole: user.userData?.roles?.[0],
            configName: 'AICA_DEBT_PRODUCT',
        })
            .then((configResponse) => {
                let headers = [{ id: 'caret', label: '-' }, ...(configResponse?.additionalDetailsConfig?.columns || [])];
                // add element in the second place
                headers.splice(1, 0, { id: 'select', label: '' });
                //Take out the Debt Products name title from dashboard config
                const debtProductName = dashboardConfig?.additionalDetailsConfig?.companyTabs?.find((tab) => tab.id === 'debtProducts')
                    ?.title;
                const showFilters = configResponse?.additionalDetailsConfig?.filters?.showFilters;
                const visibilityStates = {
                    ...configResponse?.additionalDetailsConfig?.visibilityStates,
                    debtProductName: debtProductName,
                };
                setDebtProductsData((existing) => ({
                    ...existing,
                    headers: headers,
                    visibilityStates: visibilityStates,
                }));
                setShowFilters(showFilters);
                let updatedFilters: Record<string, Record<string, number>> = {};
                const filterPages = configResponse.additionalDetailsConfig.filters.pages || [];

                if (showFilters) {
                    filterPages.forEach((page) => {
                        page.config.forEach((section) => {
                            // Ensure the sectionKey exists in updatedFilters as an object
                            if (!updatedFilters[section.sectionKey]) {
                                updatedFilters[section.sectionKey] = {};
                            }

                            Object.entries(section.config).forEach(([key, value]: [string, any]) => {
                                if (value.minimumValue !== undefined && value.maximumValue !== undefined) {
                                    const capitalizedKey = convertToFirstLetterCapital(key);
                                    updatedFilters[section.sectionKey][`minimum${capitalizedKey}`] = value.minimumValue;
                                    updatedFilters[section.sectionKey][`maximum${capitalizedKey}`] = value.maximumValue;
                                }
                            });
                        });
                    });
                    updatedFilters.filters['filterType'] = configResponse?.additionalDetailsConfig?.filters.filterType;
                    setDefaultFilters(updatedFilters);
                } else {
                    setDefaultFilters({});
                }
                setConfigLoaded(true);
            })
            .catch(() => {
                setConfigLoaded(false);
            });
    };

    const fetchCompanyMetrics = async (isClearDeal: boolean, isEditProfile: boolean = false) => {
        let companyMetricsDataExists = false;
        let companyMetricsDataExistsInAuditedFinancials = false;
        let latestMetricsData: any = {};

        const hasValidData = (data: Record<string, any>) => {
            return Object.entries(data || {}).some(([key, value]) => key !== 'externalRating' && value !== null && value !== '');
        };

        await Promise.all([
            getCompanyMetrics({
                assesseeOrgId: params.id as string,
            }).then((data) => {
                if (data) {
                    const metricsData = data.companyMetrics ?? {};
                    if (
                        !companyMetricsData?.dealTerms ||
                        !dealStatus ||
                        isClearDeal ||
                        isEditProfile ||
                        searchParams.has('draftDeleteUpdated')
                    ) {
                        setCompanyMetricsData(metricsData);
                        searchParams.delete('draftDeleteUpdated');
                        // updateQueryParams(searchParams, setSearchParams, {}, location);
                    }
                    if (Object.keys(metricsData || {}).length) {
                        companyMetricsDataExists = true;
                    }
                    latestMetricsData = metricsData;
                    setPercentageCompletion(data?.percentageCompletion);
                }
            }),

            getCompanyDealMetrics({
                requestPayload: {
                    requestId: getTimeStamp(),
                    parentOrgId: user.userData.investorOrgId,
                    orgId: params.id as string,
                    type: 'auditedfinancials',
                    startDate: null,
                    endDate: null,
                    frequency: 'Yearly',
                    metricList: AUDITED_FINANCIAL_KEY_METRIC,
                    taskStepsList: ['AION_AUDITED_FINANCIAL'],
                    mis: 'Balance Sheet',
                    raw: true,
                    classificationId: 30,
                },
            }).then((data) => {
                if (hasValidData(data)) {
                    companyMetricsDataExistsInAuditedFinancials = true;
                }
            }),
        ])
            .then(() => {
                if (companyMetricsDataExists || companyMetricsDataExistsInAuditedFinancials) {
                    setCompanyMetricsDataExists(true);
                }
            })
            .catch((error) => {
                console.error('Error fetching company metrics:', error);
            });
        return latestMetricsData;
    };

    const updateDealDataState = (value: any, key: string) => {
        setDealPayload((prevState) => {
            // Clone previous products and lenders (Map) to avoid direct mutation
            const products = [...prevState.products];
            const lenders = new Map(prevState.lenders);
            // Track how many items we're adding or removing
            let selectionDelta = 0;

            if (key === 'product') {
                // Toggle product in `products` array
                const index = products.indexOf(value);
                if (index > -1) {
                    // Item already present -> remove it
                    products.splice(index, 1);
                    // Remove the product from all lenders and update selectionDelta
                    lenders.forEach((existingProducts, lenderId) => {
                        const existingIndex = existingProducts.indexOf(value);
                        if (existingIndex > -1) {
                            existingProducts.splice(existingIndex, 1);
                            lenders.set(lenderId, existingProducts);
                            selectionDelta -= 1;
                        }
                        if (existingProducts.length === 0) {
                            lenders.delete(lenderId);
                        }
                    });
                    selectionDelta -= 1;
                } else {
                    // Item not present -> add it
                    products.push(value);
                    selectionDelta += 1;
                }
            } else if (key === 'investor') {
                // Toggle productId for a lenderId in the `lenders` Map
                const { lenderId, productId } = value;
                if (!products.includes(productId)) {
                    products.push(productId);
                    selectionDelta += 1;
                }
                const existingProducts = lenders.get(lenderId) || [];

                const existingIndex = existingProducts.indexOf(productId);
                if (existingIndex > -1) {
                    // This productId already exists for lenderId -> remove
                    existingProducts.splice(existingIndex, 1);
                    selectionDelta -= 1;

                    // If lender has no more products, remove the lender from the map
                    if (existingProducts.length === 0) {
                        lenders.delete(lenderId);
                    } else {
                        lenders.set(lenderId, existingProducts);
                    }
                } else {
                    // This productId does not exist -> add it
                    lenders.set(lenderId, [...existingProducts, productId]);
                    selectionDelta += 1;
                }
            }
            // Update the dealPayload first
            const newDealPayload = {
                ...prevState,
                products,
                lenders,
            };

            // Then adjust selectionCount by however many we added/removed
            setSelectionCount((prev) => prev + selectionDelta);

            return newDealPayload;
        });
    };

    const clearSelection = () => {
        setDealPayload({
            products: [],
            lenders: new Map(),
        });
        setSelectionCount(0);
    };

    const setPreviousDealData = () => {
        setDealPayload(structuredClone(originalDealPayloadRef.current));
        setSelectionCount(draftSelectionCount);
    };

    const fetchMandateQualifications = (selectedMandateData?: any, selectedMandateState?: string, mandateId?: string): Promise<any> => {
        return new Promise((resolve) => {
            let lenderProducts = {};
            const mandateStatus = selectedMandateState || mandateState;
            if (mandateStatus === MANDATE_STATES.SHARED) {
                lenderProducts = !isEmpty(updatedLendersMap)
                    ? Object.fromEntries(updatedLendersMap)
                    : Object.fromEntries(selectedMandateData?.lenders || dealPayload.lenders);
            } else {
                lenderProducts = Object.fromEntries(selectedMandateData?.lenders || dealPayload.lenders);
            }
            lenderProducts = {
                ...lenderProducts,
                [user.userData.investorOrgId]: selectedMandateData?.products || dealPayload.products,
            };

            getMandateQualificationChecksData(
                {
                    lenderProducts: lenderProducts,
                    orgId: params.id as string,
                    investorOrgId: user.userData.investorOrgId,
                    dealId: mandateId,
                },
                {
                    onSuccess: (data) => {
                        setMandateQualificationChecksData(data);
                        resolve(data);
                    },
                    onError: () => {
                        const errorData = { result: false, rules: [] };
                        setMandateQualificationChecksData(errorData);
                        resolve(errorData);
                    },
                },
            );
        });
    };

    const updateDealStatusOnMandateQualification = async (mandateId: string, selectedMandateData?: any, productLendersMap?: any) => {
        try {
            const selectedMandateState = selectedMandateData?.mandateState || mandateState;
            const selectedDealStatus = selectedMandateData?.dealStatus || dealStatus;
            const data = await fetchMandateQualifications(productLendersMap, selectedMandateState, mandateId);
            if (data?.result === true && selectedMandateState === MANDATE_STATES.DRAFT && selectedDealStatus === DealStatus.DRAFT) {
                dispatch(
                    updateDeal({
                        dealId: mandateId,
                        dealStatus: DealStatus.REVIEW_BASE_DATA,
                        lastUpdatedBy: getPlatformUserId(),
                        userId: user.userData.userId,
                    }),
                );
                setDealStatus(DealStatus.REVIEW_BASE_DATA);
            }
        } catch (error) {
            console.error('Error fetching mandate qualifications:', error);
        }
    };

    const saveSelection = (
        dealStatus: string,
        dealId: string,
        buttonText: string = '',
        latestMetricsData: any = {},
        isShareMandateUpdate: boolean = false,
    ) => {
        // Set DRAFT spinner only for DRAFT status
        if (dealStatus === 'DRAFT') {
            setSavingDraft(true);
        }

        const dealTerms = latestMetricsData?.dealTerms ?? companyMetricsData?.dealTerms;

        // 1. Prepare the base input to createDealData
        const payload = {
            dealTerms,
            productEnums: Array.from(new Set(dealPayload.products.map((product) => product))),
            lenderDetails: dealPayload.lenders,
        };
        // 2. Create the masterData from payload
        const masterData = createDealData(payload);

        // 3. Generate createDealPayload based on status
        const createDealPayload = getCreateDealPayload({
            dealStatus,
            dealId,
            masterData,
            platformData,
            params,
            loggedInUser,
            platformUserId,
        });

        // In case the mandate as already been shared, we do not need to store the lender details in the MANDATE as they are part of DEAL now.
        if (mandateState === MANDATE_STATES.SHARED) {
            delete createDealPayload.dealReceiverDetailMap;
        }

        // 4. Define a generic success handler for create/update
        const handleDealOperationSuccess = async (newDealId?: string) => {
            // if createDeal returned a newDealId
            if (newDealId) {
                setDealId(newDealId);
            }
            setDealStatus(dealStatus);
            setIsInitialSharedState(false);

            // If DRAFT_DELETED, reset everything
            if (dealStatus === 'DRAFT_DELETED') {
                setCompanyMetricsData({});
                setDealPayload({ products: [], lenders: new Map() });
                originalDealPayloadRef.current = { products: [], lenders: new Map() };
                setSelectionCount(0);
                setDraftSelectionCount(0);
                await fetchCompanyMetrics(true);
                setTimeout(() => {
                    setSavingDraft(false);
                }, 3000);
                return;
            }

            // Fetch the latest Mandate Dropdown data
            getDealsByStatusData(
                { orgId: params.id as string, status: validMandateDropdownStatuses },
                {
                    onSuccess: (data) => {
                        setDraftDealsData(data);
                        // Set selectedMandate by matching the dealId if data exists
                        if (data && data.length > 0) {
                            if (dealId) {
                                // Find mandate that matches the current dealId
                                const matchingMandate = data.find((mandate) => mandate.id === dealId);
                                if (matchingMandate) {
                                    setSelectedMandate(matchingMandate);
                                } else {
                                    // If no match found, set first mandate as fallback
                                    setSelectedMandate(data[0]);
                                    setDealId(data[0].id);
                                }
                            } else {
                                // If no dealId exists, set first mandate as selected
                                setSelectedMandate(data[0]);
                                setDealId(data[0].id);
                            }
                        }
                    },
                },
            );

            if (mandateState === MANDATE_STATES.SHARED) {
                const newLendersMap = compareLenders(dealPayload, originalDealPayloadRef.current);
                const combined = [...updatedLendersMap, ...newLendersMap];
                setUpdatedLendersMap(combined);
                dispatch(updateDealApplicationData({ updatedLendersMap: combined }));
            }

            // Otherwise, just update counters and spinner
            setDraftSelectionCount(selectionCount);
            originalDealPayloadRef.current = {
                products: [...dealPayload.products],
                lenders: new Map(Array.from(dealPayload.lenders.entries()).map(([lenderId, productList]) => [lenderId, [...productList]])),
            };
            setTimeout(() => {
                setSavingDraft(false);
                if (buttonText === 'Create Mandate') {
                    dispatch(
                        handleDealApplicationDrawer({
                            open: false,
                            source: 'CreateMandate',
                            dealId: '',
                            width: '',
                            heading: '',
                            subHeading: '',
                            showSuccessModal: true,
                            profileGenerationStatus: '',
                        }),
                    );
                    updateQueryParams(searchParams, setSearchParams, { createMandate: true }, location);
                }
            }, 3000);
        };

        // 5. Dispatch the create/update calls
        // For DRAFT or DRAFT_DELETED
        if (dealStatus === DealStatus.DRAFT || isShareMandateUpdate) {
            // If deal already exists -> update; otherwise -> create
            if (dealId) {
                const updatePayload = { ...createDealPayload, dealStatus: '' };
                dispatch(
                    updateDeal(updatePayload, {
                        onSuccess: () => {
                            handleDealOperationSuccess();
                            updateDealStatusOnMandateQualification(dealId);
                        },
                        onError: () => {
                            setTimeout(() => {
                                setSavingDraft(false);
                            }, 3000);
                        },
                    }),
                );
            } else {
                dispatch(
                    createDeal(createDealPayload, {
                        onSuccess: (newDealId: string) => {
                            handleDealOperationSuccess(newDealId);
                            updateDealStatusOnMandateQualification(newDealId);
                        },
                        onError: () => {
                            setTimeout(() => {
                                setSavingDraft(false);
                            }, 3000);
                        },
                    }),
                );
            }
        } else {
            // For other statuses -> always update, then open the drawer
            const updatePayload = { ...createDealPayload, dealStatus: '' };
            dispatch(
                updateDeal(updatePayload, {
                    onSuccess: () => {
                        handleDealOperationSuccess();
                        dispatch(
                            handleDealApplicationDrawer({
                                open: true,
                                width: '60%',
                                source: mandateState === MANDATE_STATES.SHARED ? 'editDeal' : 'debtProducts',
                                heading: mandateState === MANDATE_STATES.SHARED ? 'Edit Deal' : 'Create Deal',
                                subHeading: selectedCompany?.orgName,
                                profileGenerationStatus: selectedCompany.profileGenerationStatus,
                                dealId,
                                updatedLendersMap,
                            }),
                        );
                    },
                    onError: () => {
                        // Clear selection when error occurs
                        // This case can occur when multiple users are trying to share a mandate at the same time
                        handleMandateSelection(null);
                        getDealsByStatusData(
                            { orgId: params.id as string, status: validMandateDropdownStatuses },
                            {
                                onSuccess: (data) => {
                                    setDraftDealsData(data);
                                },
                            },
                        );
                    },
                }),
            );
        }

        Mixpanel.track(AICA_CREATE_DEAL, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: platformData?.investorOrgId,
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            organizationId: selectedCompany?.assesseeOrgId,
        });
    };

    /**
     * Helper function to generate the payload for deal creation/updation
     */
    function getCreateDealPayload({
        dealStatus,
        dealId,
        masterData,
        platformData,
        params,
        loggedInUser,
        platformUserId,
    }: {
        dealStatus: string;
        dealId: string;
        masterData: MasterData | undefined;
        platformData: any;
        params: any;
        loggedInUser: any;
        platformUserId: string;
    }) {
        let basePayload: any = {
            dealTerms: {
                ...masterData?.dealTerms,
                loanTypes: masterData?.dealTerms?.['loanTypes']?.map((loanType: any) => {
                    return dealProducts?.find((product: any) => product.value === loanType)?.label;
                }),
            },
            dealProposerId: platformData?.investorOrgId,
            dealBeneficiaryId: params.id as string,
            dealCreatedBy: loggedInUser?.userName,
            userId: platformUserId,
        };
        if (dealId) {
            basePayload.dealId = dealId;
        }
        basePayload.owner = {
            name: loggedInUser?.userName,
            userId: getPlatformUserId(),
            activityBy: loggedInUser?.userName,
            activityDate: new Date().toISOString(),
            ownerType: DEAL_CREATOR_TYPE,
        };

        // For DRAFT and other statuses leading to the same structure
        return {
            ...basePayload,
            products: masterData?.products,
            dealReceiverDetailMap: masterData?.dealReceiverDetailMap
                ? Object.fromEntries(
                      Array.from(masterData.dealReceiverDetailMap).map(([key, value]) => [
                          key,
                          {
                              dealTerms: {
                                  ...value.dealTerms,
                                  loanTypes: value.productEnums?.map((loanType: any) => {
                                      return dealProducts?.find((product: any) => product.value === loanType)?.label;
                                  }),
                              },
                              products: value.productEnums,
                          },
                      ]),
                  )
                : {},
            dealStatus: dealStatus,
        };
    }

    const fetchPECDataAndDebtProductsData = (payload?: any) => {
        setLoading(true);
        fetchAllPecData({
            requestPayload: {
                aggregatorId: user.userData?.investorOrgId,
                investmentPreferenceType: 'PEC',
                ...(isLender && { lenderId: user.userData?.lenderOrgId }),
            },
        })
            .then((PECdata) => {
                if (PECdata?.length) {
                    setNoPECExists(false);
                    dispatch(setDebtProductsFilters(payload));
                    fetchDebtProductsData(payload);
                } else {
                    setNoPECExists(true);
                    setLoading(false);
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const fetchDebtProductsData = (payload?: any) => {
        getDebtProductsData({
            orgId: params.id as string,
            aggregatorId: user.userData.investorOrgId,
            ...(payload ? payload : isEmpty(debtProductsFilters) ? {} : { filters: debtProductsFilters }),
        })
            .then((data) => {
                const debtProductsWithLenders = data?.filter((rowdata) => !!rowdata?.lenders?.length) || [];
                const debtProductsWithoutLenders = data?.filter((rowdata) => !rowdata?.lenders?.length) || [];

                setDebtProductsData((e) => ({
                    ...e,
                    rows: [...debtProductsWithLenders, ...debtProductsWithoutLenders],
                }));
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error in fetching Debt Products Data', err);
                setLoading(false);
            });
    };

    const registerMixpanelForRowExpand = (rowData) => {
        Mixpanel.track(EXPAND_PRODUCT, {
            user_email: user.userData.primaryEmail,
            timestamp: getTimeStamp(),
            productId: rowData?.productId,
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
        });
    };

    const registerMixpanelForUserHelpClick = (rowData, isParentRow = false) => {
        Mixpanel.track(CLICK_PROBABILITY_HELP, {
            user_email: user.userData.primaryEmail,
            timestamp: getTimeStamp(),
            productId: rowData?.productId,
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
            ...(!isParentRow && {
                lender_id: rowData?.lenderId,
                lender_name: rowData?.lenderName,
            }),
        });
    };

    const registerMixpanelForEditProfileClicked = (source) => {
        Mixpanel.track(OPEN_EDIT_PROFILE, {
            user_email: user.userData.primaryEmail,
            timestamp: getTimeStamp(),
            source: source,
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
        });
    };

    const renderQualificationStatusPill = () => {
        const isQualified = mandateQualificationChecksData?.result !== false;
        return (
            <div style={pillStyles}>
                <span>{isQualified === false ? QUALIFICATION_STATUS.UNQUALIFIED : QUALIFICATION_STATUS.QUALIFIED}</span>
                <img
                    src={isQualified === false ? infoIconRed : infoIconGreen}
                    alt={isQualified === false ? QUALIFICATION_STATUS.UNQUALIFIED : QUALIFICATION_STATUS.QUALIFIED}
                    style={{ height: '0.875rem', width: '0.875rem' }}
                />
            </div>
        );
    };

    const renderDealActionButton = (dealId: string, searchParams: URLSearchParams, setSearchParams: (params: URLSearchParams) => void) => {
        const isShareDeal = dealId && deepCompare(originalDealPayloadRef.current, dealPayload);

        let buttonText = '';
        if (isShareDeal) {
            buttonText = 'Share Mandate';
        } else if (dealId && dealStatus !== 'DRAFT_DELETED') {
            buttonText = 'Update Mandate';
        } else {
            buttonText = 'Create Mandate';
        }

        const handleClick = () => {
            if (isShareDeal) {
                Mixpanel.track(AICA_SHARE_DEAL, {
                    userRole: user?.userData?.accountType,
                    pnoPlatform: getSubdomain(),
                    pnoId: user?.userData?.investorOrgId,
                    userId: user?.userData?.userId,
                    currentUrl: window.location.href,
                    organizationId: params.id,
                });
                searchParams.set('dealCreated', 'true');
                setSearchParams(searchParams);
                saveSelection(MANDATE_STATES.SHARED, dealId);
            } else if (!dealId && isVisible(VISIBILITY_KEYS.MANDATE_CREATION.MODAL)) {
                // Dispatch modal - handleCheck in InvestorPrivateRoutes will determine if it should actually show
                dispatch(
                    setConfigTableModal({
                        show: true,
                        data: {
                            entity: ENTITY_TYPES.MANDATE,
                            actionType: 'createMandate',
                            source: [ENTITY_TYPES.LEAD, ENTITY_TYPES.MANDATE],
                            dealId: dealId,
                            orgId: params.id,
                            onSuccess: () => {
                                // After successful Lead update, save the selection to create mandate
                                saveSelection(MANDATE_STATES.DRAFT, dealId, 'Create Mandate');
                            },
                        },
                    }),
                );
            } else {
                saveSelection(mandateState || MANDATE_STATES.DRAFT, dealId, buttonText, {}, mandateState === MANDATE_STATES.SHARED);
            }
        };

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative' }}>
                {isShareDeal &&
                    (mandateQualificationChecksData?.rules?.length > 0 ? (
                        <>
                            <div style={{ position: 'relative', zIndex: 10001 }}>
                                <Tooltip
                                    content={tooltipContent}
                                    direction="left"
                                    fitContent={true}
                                    style={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        top: 'auto',
                                        left: '-60px',
                                        right: 'auto',
                                        transform: 'translateX(-50%)',
                                        marginBottom: '0.5rem',
                                        marginLeft: '0',
                                        zIndex: 99999,
                                        maxWidth: '400px',
                                        minWidth: '300px',
                                    }}
                                    contentCss={{
                                        borderRadius: '8px',
                                        position: 'relative',
                                        zIndex: 99999,
                                        padding: '1rem',
                                        boxShadow: '0px 0px 10px 2px #0000001A',
                                    }}
                                    hideDelay={400}
                                >
                                    {renderQualificationStatusPill()}
                                </Tooltip>
                            </div>
                            <div
                                style={{
                                    width: '1px',
                                    height: '2rem',
                                    backgroundColor: '#E7e7e7',
                                }}
                            />
                        </>
                    ) : (
                        <>
                            {renderQualificationStatusPill()}
                            <div
                                style={{
                                    width: '2px',
                                    height: '2rem',
                                    backgroundColor: '#E7e7e7',
                                    marginLeft: '1rem',
                                }}
                            />
                        </>
                    ))}
                <Button
                    text={buttonText}
                    disabled={Boolean(
                        !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.CREATE_DEAL) ||
                            (isShareDeal &&
                                mandateQualificationChecksData?.result === false &&
                                isVisible(VISIBILITY_KEYS.QUALIFICATION_CHECKS.ENABLE)) ||
                            (isShareDeal && mandateState === MANDATE_STATES.SHARED && isInitialSharedState),
                    )}
                    onClick={handleClick}
                    variant="primary"
                    icon={!isShareDeal ? plusWhite : ShareIcon}
                    iconStyles={{
                        height: '1rem',
                        width: '1rem',
                        margin: '0 0.5rem 0 0',
                    }}
                    style={{
                        marginTop: '0',
                        width: isShareDeal ? '9rem' : '10rem',
                    }}
                />
            </div>
        );
    };

    /**
     * Helper function to compare lenders between two objects
     * @param obj1 - The first object to compare
     * @param obj2 - The second object to compare
     * @returns An array of lenders that are different between the two objects
     */
    function compareLenders(obj1: any, obj2: any) {
        const lenders1 = obj1.lenders || new Map();
        const lenders2 = obj2.lenders || new Map();
        const diff: [string, string[]][] = [];

        for (const [lenderId, products] of lenders1.entries()) {
            if (!lenders2.has(lenderId)) {
                diff.push([lenderId, products]);
            }
        }

        return diff;
    }

    const renderClearOrDeleteButton = (dealId: string) => {
        if (!dealId || dealStatus === 'DRAFT_DELETED') return null;
        const isExactMatch = deepCompare(originalDealPayloadRef.current, dealPayload);
        if (isExactMatch) return null;
        const buttonText = isExactMatch ? 'Delete Mandate' : 'Discard Changes';
        const handleClick = () => {
            if (isExactMatch) {
                setShowDeleteModal(true);
                updateQueryParams(searchParams, setSearchParams, { deleteMandate: true });
            } else {
                setPreviousDealData();
                setShowDeleteModal(false);
            }
        };

        return (
            <Button
                text={buttonText}
                onClick={handleClick}
                variant="secondary"
                iconStyles={{
                    height: '1rem',
                    width: '1rem',
                    margin: '0 0.5rem 0 0',
                }}
                style={{ marginTop: '0', width: '9rem' }}
            />
        );
    };

    const renderMessage = () => {
        const isShareDeal = dealId && deepCompare(originalDealPayloadRef.current, dealPayload);

        if (isShareDeal) {
            return 'Share mandate with lenders';
        } else if (dealId && dealStatus !== 'DRAFT_DELETED') {
            return 'Update mandate for selected products ';
        } else {
            return 'Create mandate for selected products';
        }
    };

    /**
     * Determines if a checkbox should be disabled based on mandate state and selection state
     * Uses originalDealPayloadRef which contains the original data from getDealData response
     * @param rowData - The row data for the checkbox
     * @param isParentRow - Whether this is a parent row (product) or child row (lender)
     * @returns true if checkbox should be disabled, false otherwise
     */
    const isCheckboxDisabled = (rowData: DebtProductRowData, isParentRow: boolean): boolean => {
        if (mandateState !== MANDATE_STATES.SHARED) return false;
        const originalPayload = originalDealPayloadRef.current;
        if (isParentRow) {
            // For parent rows, check if product was originally selected in the API response
            return originalPayload.products?.includes(rowData.productId) || false;
        } else {
            // For child rows, disable lenders if the lenderId exists in originalPayload.lenders
            if (!rowData.lenderId) {
                return false;
            }
            // Check if the lenderId exists as a key in originalPayload.lenders
            return originalPayload.lenders.has(rowData.lenderId);
        }
    };

    return {
        loading,
        noPecExists,
        companyMetricsDataExists,
        showDetailOverview,
        setShowDetailOverview,
        debtProductsData,
        showEmptyScreen,
        dealDrawerState,
        setDebtProductsData,
        fetchPECDataAndDebtProductsData,
        fetchCompanyMetrics,
        updateDealDataState,
        dealPayload,
        selectionCount,
        clearSelection,
        saveSelection,
        savingDraft,
        fetchDebtProductsData,
        dealId,
        draftSelectionCount,
        setPreviousDealData,
        //Mixpanel
        registerMixpanelForRowExpand,
        registerMixpanelForUserHelpClick,
        registerMixpanelForEditProfileClicked,
        showFilters,
        renderDealActionButton,
        renderClearOrDeleteButton,
        renderMessage,
        showDeleteModal,
        setShowDeleteModal,
        dealStatus,
        originalDealPayloadRef,
        draftDealsData,
        dealStatusConfig,
        selectedMandate,
        handleMandateSelection,
        isCheckboxDisabled,
        mandateState,
        percentageCompletion,
        // Probability tooltip
        getProbabilityTooltip,
        PROBABILITY_TOOLTIP_MESSAGES,
    };
};

export default useDebtProductLogic;
